import { defineStore } from 'pinia'
import axios from '../config/api.js'
import { unwrapCollection, LD_HEADERS } from './apiCollection.js'

// Taille de page fixe côté API Platform (paramètre `limit` ignoré par le serveur)
const API_PAGE_SIZE = 30

// Mappe un Customer API Platform (snake_case) vers le modèle attendu par la vue.
// Les champs sans source API (email, téléphone, statut, CA, commandes...) restent à null
// pour que la vue affiche « — » au lieu d'inventer une valeur.
export function normalizeCustomer(raw = {}) {
  const country = raw.country_code ?? raw.address?.country ?? null
  return {
    ...raw,
    id: raw.id,
    name: raw.customer_name ?? raw.name ?? '',
    email: raw.email ?? null,
    phone: raw.phone ?? null,
    contactPerson: raw.contactPerson ?? null,
    type: raw.type ?? null,
    thirdParty: raw.third_party ?? null,
    lupCode: raw.lup_code ?? null,
    taxNumber: raw.tax_number || null,
    status: raw.status ?? null,
    address: raw.address ?? (country ? { street: '', postalCode: '', city: '', country } : null),
    ordersCount: raw.ordersCount ?? null,
    totalRevenue: raw.totalRevenue ?? null,
    averageOrderValue: raw.averageOrderValue ?? null,
    lastOrderDate: raw.lastOrderDate ?? null,
    createdAt: raw.createdAt ?? null
  }
}

export const useCustomersStore = defineStore('customers', {
  state: () => ({
    customers: [],
    loading: false,
    error: null,
    stats: {
      total: 0,
      active: 0,
      inactive: 0,
      prospects: 0,
      withOrders: 0,
      totalRevenue: 0,
      averageRevenue: 0
    },
    pagination: {
      page: 1,
      limit: API_PAGE_SIZE,
      total: 0
    }
  }),

  getters: {
    activeCustomers: (state) => {
      return state.customers.filter(customer => customer.status === 'active')
    },
    
    inactiveCustomers: (state) => {
      return state.customers.filter(customer => customer.status === 'inactive')
    },
    
    prospects: (state) => {
      return state.customers.filter(customer => customer.status === 'prospect')
    },
    
    companiesCustomers: (state) => {
      return state.customers.filter(customer => customer.type === 'company')
    },
    
    individualCustomers: (state) => {
      return state.customers.filter(customer => customer.type === 'individual')
    },
    
    customersWithOrders: (state) => {
      return state.customers.filter(customer => customer.ordersCount > 0)
    },
    
    topCustomersByRevenue: (state) => {
      return [...state.customers]
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 10)
    },
    
    recentCustomers: (state) => {
      return [...state.customers]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 10)
    },
    
    customersByCountry: (state) => {
      const countries = {}
      state.customers.forEach(customer => {
        if (customer.address?.country) {
          const country = customer.address.country
          countries[country] = (countries[country] || 0) + 1
        }
      })
      return countries
    }
  },

  actions: {
    async loadCustomers(page = 1) {
      this.loading = true
      this.error = null
      
      try {
        // Appel API réel
        const response = await axios.get('/api/customers', {
          params: {
            page,
            limit: this.pagination.limit
          },
          headers: LD_HEADERS
        })
        
        const { items, totalItems } = unwrapCollection(response.data)
        this.customers = items.map(normalizeCustomer)
        this.pagination.total = totalItems ?? ((page - 1) * this.pagination.limit + this.customers.length)
        this.pagination.page = page
        this.updateStats()
        
      } catch (error) {
        this.error = 'Erreur lors du chargement des clients'
        console.error('Erreur loadCustomers:', error)
      } finally {
        this.loading = false
      }
    },

    updateStats() {
      // Un indicateur n'est calculé que si le champ source existe dans les données API ;
      // sinon il vaut null (affiché « — »).
      const has = (field) => this.customers.some(c => c[field] !== null && c[field] !== undefined)
      const revenues = this.customers
        .filter(c => c.totalRevenue !== null && c.totalRevenue !== undefined && c.totalRevenue !== '')
        .map(c => Number(c.totalRevenue))
        .filter(Number.isFinite)
      const revenueSum = revenues.reduce((sum, v) => sum + v, 0)
      this.stats = {
        total: this.pagination.total || this.customers.length,
        active: has('status') ? this.customers.filter(c => c.status === 'active').length : null,
        inactive: has('status') ? this.customers.filter(c => c.status === 'inactive').length : null,
        prospects: has('status') ? this.customers.filter(c => c.status === 'prospect').length : null,
        withOrders: has('ordersCount') ? this.customers.filter(c => c.ordersCount > 0).length : null,
        totalRevenue: revenues.length ? revenueSum : null,
        averageRevenue: revenues.length ? Math.round(revenueSum / revenues.length) : null
      }
    },

    async createCustomer(customerData) {
      try {
        const response = await axios.post('/api/customers', customerData)
        const newCustomer = normalizeCustomer(response.data)
        
        this.customers.unshift(newCustomer)
        this.updateStats()
        
        return newCustomer
        
      } catch (error) {
        this.error = 'Erreur lors de la création du client'
        console.error('Erreur createCustomer:', error)
        throw error
      }
    },

    async updateCustomer(customerId, customerData) {
      try {
        const response = await axios.put(`/api/customers/${customerId}`, customerData)
        const updatedCustomer = normalizeCustomer(response.data)
        
        const index = this.customers.findIndex(c => c.id === customerId)
        if (index !== -1) {
          this.customers[index] = updatedCustomer
          this.updateStats()
        }
        
        return updatedCustomer
        
      } catch (error) {
        this.error = 'Erreur lors de la mise à jour du client'
        console.error('Erreur updateCustomer:', error)
        throw error
      }
    },

    async deleteCustomer(customerId) {
      try {
        await axios.delete(`/api/customers/${customerId}`)
        
        const index = this.customers.findIndex(c => c.id === customerId)
        if (index !== -1) {
          this.customers.splice(index, 1)
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors de la suppression du client'
        console.error('Erreur deleteCustomer:', error)
        throw error
      }
    },

    async getCustomerById(customerId) {
      try {
        // Vérifier d'abord dans le store local
        let customer = this.customers.find(c => c.id === customerId)
        
        if (!customer) {
          // Appel API si pas trouvé localement
          const response = await axios.get(`/api/customers/${customerId}`)
          customer = normalizeCustomer(response.data)
        }
        
        return customer
        
      } catch (error) {
        this.error = 'Erreur lors de la récupération du client'
        console.error('Erreur getCustomerById:', error)
        throw error
      }
    },

    async searchCustomers(query, filters = {}) {
      try {
        const response = await axios.get('/api/customers/search', { 
          params: { q: query, ...filters } 
        })
        return response.data
        
      } catch (error) {
        this.error = 'Erreur lors de la recherche'
        console.error('Erreur searchCustomers:', error)
        throw error
      }
    },

    async exportCustomers(format = 'csv', filters = {}) {
      try {
        const response = await axios.post('/api/customers/export', { format, filters })
        
        // Créer le téléchargement du fichier
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `clients_${new Date().toISOString().split('T')[0]}.${format}`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        return {
          filename: `clients_${new Date().toISOString().split('T')[0]}.${format}`,
          success: true
        }
        
      } catch (error) {
        this.error = 'Erreur lors de l\'export'
        console.error('Erreur exportCustomers:', error)
        throw error
      }
    },

    generateCSV(customers) {
      const headers = [
        'ID', 'Nom/Entreprise', 'Email', 'Téléphone', 'Contact', 'Type', 'Statut',
        'Adresse', 'Code Postal', 'Ville', 'Pays', 'Nb Commandes', 'CA Total',
        'Panier Moyen', 'Dernière Commande', 'Date Création'
      ]
      
      const csvRows = [headers.join(',')]
      
      customers.forEach(customer => {
        const row = [
          customer.id,
          `"${customer.name}"`,
          customer.email,
          customer.phone || '',
          customer.contactPerson || '',
          customer.type,
          customer.status,
          customer.address?.street || '',
          customer.address?.postalCode || '',
          customer.address?.city || '',
          customer.address?.country || '',
          customer.ordersCount,
          customer.totalRevenue,
          customer.averageOrderValue,
          customer.lastOrderDate || '',
          customer.createdAt
        ]
        csvRows.push(row.join(','))
      })
      
      return csvRows.join('\n')
    },

    async getCustomerStats(customerId) {
      try {
        const response = await axios.get(`/api/customers/${customerId}/stats`)
        return response.data
        
      } catch (error) {
        this.error = 'Erreur lors de la récupération des statistiques'
        console.error('Erreur getCustomerStats:', error)
        throw error
      }
    },

    async updateCustomerStatus(customerId, status) {
      try {
        await axios.patch(`/api/customers/${customerId}`, { status })
        
        const customer = this.customers.find(c => c.id === customerId)
        if (customer) {
          customer.status = status
          customer.updatedAt = new Date().toISOString()
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors de la mise à jour du statut'
        console.error('Erreur updateCustomerStatus:', error)
        throw error
      }
    },

    async mergeCustomers(primaryCustomerId, secondaryCustomerId) {
      try {
        const response = await axios.post('/api/customers/merge', { 
          primaryId: primaryCustomerId, 
          secondaryId: secondaryCustomerId 
        })
        
        // Mettre à jour les données locales avec la réponse de l'API
        const mergedCustomer = response.data
        const primaryIndex = this.customers.findIndex(c => c.id === primaryCustomerId)
        const secondaryIndex = this.customers.findIndex(c => c.id === secondaryCustomerId)
        
        if (primaryIndex !== -1) {
          this.customers[primaryIndex] = mergedCustomer
        }
        
        if (secondaryIndex !== -1) {
          this.customers.splice(secondaryIndex, 1)
        }
        
        this.updateStats()
        
      } catch (error) {
        this.error = 'Erreur lors de la fusion des clients'
        console.error('Erreur mergeCustomers:', error)
        throw error
      }
    },

    clearError() {
      this.error = null
    },

    setPagination(page, limit = 50) {
      this.pagination.page = page
      this.pagination.limit = limit
    }
  }
})